/**
 * Soft rules for sorting Cogo artikel choices.
 * Never auto-picks / never skips the user — sort only.
 * Primary sort: cheapest unit price (price per quantity). Soft biases break ties.
 */

import type { UnitType } from './cogoUnits'

export type RankableArtikel = {
  technicalProductId: string
  display: string
  unitTypes?: UnitType[] | null
  isBio?: boolean | null
  priceCents?: number | null
  weightConversionFactor?: number | null
  packSize?: number | null
  favorite?: boolean | null
}

const TIE_PER_KG = 40
const TIE_BIO = 25
const TIE_FAVORITE = 10

function offersPerKg(a: RankableArtikel): boolean {
  return Array.isArray(a.unitTypes) && a.unitTypes.includes('W')
}

function isBioHit(a: RankableArtikel): boolean {
  if (a.isBio) return true
  return /\bbio\b/i.test(a.display || '')
}

function priceOrMax(a: RankableArtikel): number {
  return a.priceCents != null && a.priceCents > 0
    ? a.priceCents
    : Number.POSITIVE_INFINITY
}

/** Cents per comparable unit: €/kg from weightConversionFactor, else €/piece from packSize, else shelf price. */
function pricePerQuantity(a: RankableArtikel): number {
  const price = priceOrMax(a)
  if (!Number.isFinite(price)) return price
  if (a.weightConversionFactor != null && a.weightConversionFactor > 0) {
    return (price * 1000) / a.weightConversionFactor
  }
  if (a.packSize != null && a.packSize > 0) {
    return price / a.packSize
  }
  return price
}

/**
 * Sort choice options: lowest price-per-quantity first, then soft-rule ties.
 * `neededGrams` is reserved for future pack-fit scoring; unused for now.
 */
export function rankCandidateOptions<T extends RankableArtikel>(
  options: T[],
  _neededGrams?: number | null
): T[] {
  if (options.length <= 1) return options.slice()

  const scored = options.map((o, index) => {
    let tie = 0
    if (offersPerKg(o)) tie += TIE_PER_KG
    if (isBioHit(o)) tie += TIE_BIO
    if (o.favorite) tie += TIE_FAVORITE
    return { o, unitPrice: pricePerQuantity(o), tie, index }
  })

  scored.sort(
    (a, b) => a.unitPrice - b.unitPrice || b.tie - a.tie || a.index - b.index
  )
  return scored.map((s) => s.o)
}

/** Prefer weight-sold artikelen when filtering (not silent pick). */
export function preferWeightSoldArticles<
  T extends { unitTypes?: UnitType[] | null },
>(candidates: T[]): T[] {
  if (candidates.length <= 1) return candidates
  const weight = candidates.filter(
    (c) => Array.isArray(c.unitTypes) && c.unitTypes.includes('W')
  )
  return weight.length > 0 ? weight : candidates
}
